import IMessageHandler from "./IMessageHandler";

/*
 * QUANTA (2026-10-22): in-page stub engine for the live preview build.
 *
 * When Constants.WS_URL starts with "local://" (the preview build sets
 * REACT_APP_WS_URL=local://stub), WebSocketClient routes here instead of a
 * real socket. This is a TypeScript port of bobba-pivot/stub-server/stub.js —
 * the same reverse-engineered Bobba text protocol, answered from in-memory
 * state inside the page, so the published preview is fully playable
 * (login -> room -> catalogue/inventory -> place/move/pick up -> sit)
 * with no backend. The local dev flow still uses the real stub server on
 * ws://127.0.0.1:8585.
 */

const C = {
    LOGIN: 1, HEIGHT_MAP: 2, MOVEMENT: 7, CHAT: 9, WAVE: 13,
    ROOM_DATA: 15, ITEM_INTERACT: 18, ITEM_MOVE: 19, ITEM_PICK_UP: 20,
    CHANGE_LOOKS: 21, CHANGE_MOTTO: 22, INVENTORY: 23, ITEM_PLACE: 24,
    CAT_INDEX: 25, CAT_PAGE: 26, CAT_PURCHASE: 27, NAV_POPULAR: 28,
    NAV_OWN: 29, NAV_SEARCH: 30, NAV_LEAVE: 33, NAV_GO: 34, NAV_CREATE: 35,
    MSG_LOAD: 42,
};
const S = {
    LOGIN_OK: 3, HEIGHTMAP: 4, PLAYERS: 6, PLAYER_STATUS: 8, CHAT: 10,
    PLAYER_WAVE: 14, FLOOR_ITEMS: 16, ITEM_REMOVE: 17,
    ITEM_STATE: 19, WALL_ITEMS: 20, INVENTORY: 21, INVENTORY_REMOVE: 22,
    CAT_INDEX: 23, CAT_PAGE: 24, CAT_PURCHASE_ERROR: 25, CAT_PURCHASE_INFO: 26,
    CREDITS: 27, MODEL_INFO: 28, ROOM_DATA: 29, ROOM_LIST: 30, NAV_LEAVE: 31,
    MSG_FRIENDS: 32,
};

interface InvItem { itemId: number; baseId: number; type: string; state: number; }
interface PlacedItem extends InvItem { x: number; y: number; z: number; rot: number; }

const ROOM = { id: 1, name: "Quanta Proof Room", owner: "Quanta", desc: "Live preview room (in-page stub)", cols: 10, rows: 8, doorX: 0, doorY: 3, capacity: 25 };
const BASE_ITEMS: { [id: number]: { name: string; seat: boolean; seatHeight: number; states: number } } = {
    1001: { name: "quanta_chair", seat: true, seatHeight: 0.45, states: 1 },
};
const CATALOGUE = [
    { pageId: 2, itemId: 1, name: "Quanta Chair", cost: 25, type: "F", baseId: 1001, amount: 1 },
];

class Out {
    parts: string[];
    constructor(id: number) { this.parts = [String(id)]; }
    int(i: number): Out { this.parts.push(String(Math.trunc(i))); return this; }
    float(f: number): Out { this.parts.push(String(f).replace(",", ".")); return this; }
    bool(b: boolean): Out { return this.int(b ? 1 : 0); }
    str(s: string): Out {
        let tickets = 0;
        for (let i = 0; i < s.length; i++) { if (s.charAt(i) === "|") { tickets++; } }
        this.parts.push(String(tickets), s);
        return this;
    }
    body(): string { return this.parts.join("|"); }
}

class In {
    tokens: string[];
    pointer: number;
    id: number;
    constructor(raw: string) {
        this.tokens = String(raw).split("|");
        this.pointer = 0;
        this.id = this.popInt();
    }
    popToken(): string { return this.pointer < this.tokens.length ? this.tokens[this.pointer++] : ""; }
    popInt(): number { const n = parseInt(this.popToken(), 10); return isNaN(n) ? 0 : n; }
    popStr(): string {
        const tickets = this.popInt();
        let s = this.popToken();
        for (let i = 0; i < tickets; i++) { s += "|" + this.popToken(); }
        return s;
    }
}

export default class LocalStubEngine {
    handler: IMessageHandler;
    outbox: string[];
    drainScheduled: boolean;
    nextItemId: number;
    user: {
        id: number; vid: number; name: string; look: string; motto: string;
        x: number; y: number; z: number; rot: number; credits: number;
        inventory: Map<number, InvItem>; placed: Map<number, PlacedItem>;
    };

    constructor(handler: IMessageHandler) {
        this.handler = handler;
        this.outbox = [];
        this.drainScheduled = false;
        this.nextItemId = 5000;
        this.user = {
            id: 1, vid: 1, name: "Guest", look: "hd-180-1021", motto: "live preview",
            x: ROOM.doorX, y: ROOM.doorY, z: 0, rot: 2, credits: 1000,
            inventory: new Map<number, InvItem>(), placed: new Map<number, PlacedItem>(),
        };
        const starter: InvItem = { itemId: ++this.nextItemId, baseId: 1001, type: "F", state: 0 };
        this.user.inventory.set(starter.itemId, starter);
    }

    open(onOpen: () => void) {
        window.setTimeout(onOpen, 10);
    }

    private push(out: Out) {
        this.outbox.push(out.body());
        if (!this.drainScheduled) {
            this.drainScheduled = true;
            window.setTimeout(() => this.drain(), 1);
        }
    }

    private drain() {
        const msg = this.outbox.shift();
        if (msg != null) {
            this.handler.handleMessage(msg);
        }
        if (this.outbox.length > 0) {
            window.setTimeout(() => this.drain(), 1);
        } else {
            this.drainScheduled = false;
        }
    }

    private serializeInventoryItem(out: Out, item: InvItem) {
        out.int(item.itemId).str(item.type).int(item.baseId).int(item.state).bool(true);
    }

    private serializeFloorItem(out: Out, item: PlacedItem) {
        out.int(item.itemId).int(item.x).int(item.y).float(item.z).int(item.rot).int(item.baseId).int(item.state);
    }

    private serializeRoom(out: Out, userCount: number) {
        out.int(ROOM.id).str(ROOM.name).str(ROOM.owner).str(ROOM.desc).int(0).int(userCount).int(ROOM.capacity);
    }

    private sendPlayers() {
        const u = this.user;
        const out = new Out(S.PLAYERS).int(1);
        out.int(u.vid).int(u.x).int(u.y).float(u.z).int(u.rot).str(u.name).str(u.look).str(u.motto);
        this.push(out);
    }

    private sendStatus(statuses: { [k: string]: string }) {
        const u = this.user;
        const out = new Out(S.PLAYER_STATUS).int(1);
        out.int(u.vid).int(u.x).int(u.y).float(u.z).int(u.rot);
        const keys = Object.keys(statuses);
        out.int(keys.length);
        keys.forEach((k) => { out.str(k).str(statuses[k]); });
        this.push(out);
    }

    private sendFloorItems() {
        const items = Array.from(this.user.placed.values());
        const out = new Out(S.FLOOR_ITEMS).int(items.length);
        items.forEach((it) => this.serializeFloorItem(out, it));
        this.push(out);
        this.push(new Out(S.WALL_ITEMS).int(0));
    }

    private sendInventory() {
        const items = Array.from(this.user.inventory.values());
        const out = new Out(S.INVENTORY).int(items.length);
        items.forEach((it) => this.serializeInventoryItem(out, it));
        this.push(out);
    }

    receive(raw: string) {
        let msg: In;
        try {
            msg = new In(raw);
        } catch (e) {
            return;
        }
        const u = this.user;
        switch (msg.id) {
            case C.LOGIN: {
                u.name = msg.popStr() || "Guest";
                u.look = msg.popStr() || u.look;
                this.push(new Out(S.LOGIN_OK).int(u.id).str(u.name).str(u.look).str(u.motto));
                this.push(new Out(S.CREDITS).int(u.credits));
                break;
            }
            case C.INVENTORY: this.sendInventory(); break;
            case C.CAT_INDEX: {
                const out = new Out(S.CAT_INDEX).int(1);
                out.bool(true).int(0).int(0).int(1).str("Quanta").int(1);
                out.bool(true).int(0).int(0).int(2).str("Furniture").int(0);
                this.push(out);
                break;
            }
            case C.CAT_PAGE: {
                const pageId = msg.popInt();
                const items = CATALOGUE.filter((i) => i.pageId === pageId);
                const out = new Out(S.CAT_PAGE).int(pageId)
                    .str("default").str("").str("").str("Quanta catalogue").str("Proof items").str("").str("")
                    .int(items.length);
                items.forEach((i) => out.int(i.itemId).str(i.name).int(i.cost).str(i.type).int(i.baseId).int(i.amount));
                this.push(out);
                break;
            }
            case C.CAT_PURCHASE: {
                const pageId = msg.popInt(); const itemId = msg.popInt();
                const cat = CATALOGUE.filter((i) => i.pageId === pageId && i.itemId === itemId)[0];
                if (cat == null || u.credits < cat.cost) {
                    this.push(new Out(S.CAT_PURCHASE_ERROR).bool(true));
                    break;
                }
                u.credits -= cat.cost;
                const item: InvItem = { itemId: ++this.nextItemId, baseId: cat.baseId, type: cat.type, state: 0 };
                u.inventory.set(item.itemId, item);
                this.push(new Out(S.CAT_PURCHASE_INFO).int(cat.itemId).str(BASE_ITEMS[cat.baseId].name).int(cat.cost).str(cat.type).int(cat.baseId));
                const out = new Out(S.INVENTORY).int(1); this.serializeInventoryItem(out, item); this.push(out);
                this.push(new Out(S.CREDITS).int(u.credits));
                break;
            }
            case C.NAV_POPULAR: case C.NAV_OWN: case C.NAV_SEARCH: {
                if (msg.id === C.NAV_SEARCH) { msg.popStr(); }
                const out = new Out(S.ROOM_LIST).int(1); this.serializeRoom(out, 1); this.push(out);
                break;
            }
            case C.NAV_GO: {
                msg.popInt();
                this.push(new Out(S.MODEL_INFO).str("quanta_model").int(ROOM.id));
                break;
            }
            case C.NAV_CREATE: {
                msg.popStr(); msg.popStr();
                this.push(new Out(S.MODEL_INFO).str("quanta_model").int(ROOM.id));
                break;
            }
            case C.HEIGHT_MAP: {
                const out = new Out(S.HEIGHTMAP).int(ROOM.cols).int(ROOM.rows).int(ROOM.doorX).int(ROOM.doorY);
                for (let x = 0; x < ROOM.cols; x++) { for (let y = 0; y < ROOM.rows; y++) { out.int(1); } }
                this.push(out);
                break;
            }
            case C.ROOM_DATA: {
                const out = new Out(S.ROOM_DATA); this.serializeRoom(out, 1); this.push(out);
                this.sendPlayers();
                this.sendFloorItems();
                break;
            }
            case C.MOVEMENT: {
                const x = msg.popInt(); const y = msg.popInt();
                if (x < 0 || y < 0 || x >= ROOM.cols || y >= ROOM.rows) { break; }
                u.x = x; u.y = y;
                const chair = Array.from(u.placed.values()).filter((it) => it.x === x && it.y === y && BASE_ITEMS[it.baseId] != null && BASE_ITEMS[it.baseId].seat)[0];
                if (chair != null) {
                    u.z = chair.z; u.rot = chair.rot;
                    this.sendStatus({ sit: String(BASE_ITEMS[chair.baseId].seatHeight) });
                } else {
                    u.z = 0;
                    this.sendStatus({});
                }
                break;
            }
            case C.CHAT: {
                const text = msg.popStr();
                this.push(new Out(S.CHAT).int(u.vid).str(text));
                break;
            }
            case C.WAVE: this.push(new Out(S.PLAYER_WAVE).int(u.vid)); break;
            case C.ITEM_PLACE: {
                const itemId = msg.popInt(); const x = msg.popInt(); const y = msg.popInt(); const rot = msg.popInt();
                const inv = u.inventory.get(itemId);
                if (inv == null) { break; }
                u.inventory.delete(itemId);
                const placed: PlacedItem = { itemId: itemId, baseId: inv.baseId, type: inv.type, state: inv.state, x: x, y: y, z: 0, rot: rot };
                u.placed.set(itemId, placed);
                this.push(new Out(S.INVENTORY_REMOVE).int(itemId));
                const out = new Out(S.FLOOR_ITEMS).int(1); this.serializeFloorItem(out, placed); this.push(out);
                break;
            }
            case C.ITEM_MOVE: {
                const itemId = msg.popInt(); const x = msg.popInt(); const y = msg.popInt(); const rot = msg.popInt();
                const it = u.placed.get(itemId);
                if (it == null) { break; }
                it.x = x; it.y = y; it.rot = rot;
                const out = new Out(S.FLOOR_ITEMS).int(1); this.serializeFloorItem(out, it); this.push(out);
                break;
            }
            case C.ITEM_PICK_UP: {
                const itemId = msg.popInt();
                const it = u.placed.get(itemId);
                if (it == null) { break; }
                u.placed.delete(itemId);
                const back: InvItem = { itemId: itemId, baseId: it.baseId, type: it.type, state: it.state };
                u.inventory.set(itemId, back);
                this.push(new Out(S.ITEM_REMOVE).int(itemId));
                const out = new Out(S.INVENTORY).int(1); this.serializeInventoryItem(out, back); this.push(out);
                break;
            }
            case C.ITEM_INTERACT: {
                const itemId = msg.popInt();
                const it = u.placed.get(itemId);
                if (it == null) { break; }
                const states = BASE_ITEMS[it.baseId] != null ? BASE_ITEMS[it.baseId].states : 1;
                it.state = states > 1 ? (it.state + 1) % states : 0;
                this.push(new Out(S.ITEM_STATE).int(itemId).int(it.state));
                break;
            }
            case C.CHANGE_LOOKS: {
                u.look = msg.popStr() || u.look; msg.popStr();
                this.sendPlayers();
                break;
            }
            case C.CHANGE_MOTTO: {
                u.motto = msg.popStr() || u.motto;
                this.sendPlayers();
                break;
            }
            case C.NAV_LEAVE: this.push(new Out(S.NAV_LEAVE)); break;
            case C.MSG_LOAD: this.push(new Out(S.MSG_FRIENDS).int(0)); break;
            default: break;
        }
    }
}
